/**
 * Componente de Bloqueio de Feature Premium
 * Exibe mensagem quando usuário tenta acessar feature não habilitada
 */

import React from 'react';
import { Lock, Sparkles, Check, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PremiumFeatureBlockProps {
  featureName: string;
  description: string;
  benefits: string[];
  price?: string;
  priceUnit?: string;  // NOVO: unidade do preço
  icon?: React.ReactNode;
}

export function PremiumFeatureBlock({
  featureName,
  description,
  benefits,
  price,
  priceUnit,
  icon
}: PremiumFeatureBlockProps) {
  const navigate = useNavigate();

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6">
      <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden">
        {/* Header com gradiente */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white">
          <div className="flex items-center justify-center mb-4">
            {icon || <Lock className="w-16 h-16" />}
          </div>
          <h1 className="text-3xl font-bold text-center mb-2">
            {featureName}
          </h1>
          <p className="text-center text-blue-100">
            {description}
          </p>
        </div>

        {/* Conteúdo */}
        <div className="p-8">
          {/* Badge Premium */}
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-semibold text-sm">
              <Sparkles className="w-4 h-4 mr-2" />
              Feature Premium
            </span>
          </div>

          {/* Preço */}
          {price && (
            <div className="text-center mb-8">
              <p className="text-4xl font-bold text-gray-900 dark:text-white">
                {price}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {priceUnit || 'por mês'}
              </p>
            </div>
          )}

          {/* Benefícios */}
          <div className="space-y-4 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Recursos incluídos:
            </h2>
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 dark:bg-green-900">
                    <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <p className="ml-3 text-gray-700 dark:text-gray-300">
                  {benefit}
                </p>
              </div>
            ))}
          </div>

          {/* Mensagem de contato */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
            <p className="text-center text-gray-700 dark:text-gray-300">
              <span className="font-semibold">Esta é uma feature premium.</span>
              <br />
              Entre em contato com o administrador do sistema para habilitar este recurso em sua organização.
            </p>
          </div>

          {/* Botão Voltar */}
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full flex items-center justify-center px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
